import { Box, Button, SelectChangeEvent, SimpleSelect } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { ComponentType, useCallback, useMemo, useState } from 'react';
import { ContextLinkConfig } from '@/plugin-system/model';
import { ContextLinkDefinition } from '../../../core';
import { useContextLinkTypes, useLinkTypeByUrl, useLinkTypeWithEditor } from './ContextLinkTypesProvider';

type EditorControlsType = ComponentType<{
  getLink: () => ContextLinkDefinition;
  isValid: boolean;
}>;

export interface LinkEditorProps {
  initialLink?: ContextLinkDefinition;
  Controls: EditorControlsType;
  contextLinkConfig?: ContextLinkConfig;
}

export type LinkEditorType<T extends object = Record<string, unknown>> = ComponentType<LinkEditorProps & T>;

interface ContextLinkEditorProps extends Omit<LinkEditorProps, 'Controls'> {
  onSave: (link: ContextLinkDefinition) => void;
  onCancel: () => void;
  saveLabel?: string;
  cancelLabel?: string;
}

export const ContextLinkEditor = ({
  initialLink,
  onSave,
  onCancel,
  saveLabel,
  cancelLabel,
  contextLinkConfig,
}: ContextLinkEditorProps) => {
  const { panwRebrandStubYear } = useFeatureFlags();
  const typesWithEditor = useContextLinkTypes().filter((type) => type.Editor !== undefined);
  const typeFromSpec = useLinkTypeByUrl(initialLink?.spec.url);
  const typeFromSpecHasEditor =
    typeFromSpec !== undefined && typesWithEditor.findIndex((type) => type.id === typeFromSpec.id) >= 0;
  const [type, setType] = useState<string | undefined>(typeFromSpecHasEditor === true ? typeFromSpec.id : undefined);
  const selectedLinkType = useLinkTypeWithEditor(type);

  const options = useMemo(() => {
    return typesWithEditor.map((type) => ({ key: type.id, label: type.name }));
  }, [typesWithEditor]);

  const onChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setType(event.target.value);
    },
    [setType]
  );

  const renderControls = useCallback(
    ({ getLink, isValid }: { getLink: () => ContextLinkDefinition; isValid: boolean }) => {
      return (
        <>
          {panwRebrandStubYear && (
            <Button key="cancel" onClick={onCancel} variant="secondary">
              {cancelLabel}
            </Button>
          )}
          <Button key="save" disabled={!isValid} onClick={() => onSave(getLink())}>
            {saveLabel}
          </Button>
          {!panwRebrandStubYear && (
            <Button key="cancel" onClick={onCancel} variant="secondary">
              {cancelLabel}
            </Button>
          )}
        </>
      );
    },
    [onSave, onCancel, saveLabel, cancelLabel, panwRebrandStubYear]
  );

  return (
    <div>
      <Box mb={1} width="100%">
        <SimpleSelect
          sx={{ width: '100%', maxWidth: 500 }}
          fullWidth={true}
          options={options}
          onChange={onChange}
          value={selectedLinkType.id}
          label="Link type"
        />
      </Box>
      <div>
        <selectedLinkType.Editor
          initialLink={initialLink}
          Controls={renderControls}
          contextLinkConfig={contextLinkConfig}
        />
      </div>
    </div>
  );
};
